/**
 * Pure merge of `lead_events` and `messages` into one newest-first timeline
 * for the Activity tab. Ordering is by `created_at` (ISO UTC strings compare
 * lexically); ties keep events before messages so "Assessment sent" sits
 * above the text it produced.
 */
import type { LeadEvent, Message, MessageStatus, Prompt } from '@/features/pipeline/types';

export type ActivityItem =
  | { kind: 'event'; at: string; event: LeadEvent }
  | { kind: 'prompt'; at: string; prompt: Prompt }
  | { kind: 'message'; at: string; message: Message };

/** Tie order, top to bottom: the event, then the nudge prompt it opened, then the text it produced. */
const TIE_RANK: Record<ActivityItem['kind'], number> = { event: 0, prompt: 1, message: 2 };

export function mergeActivity(events: LeadEvent[], messages: Message[], prompts: Prompt[] = []): ActivityItem[] {
  const items: ActivityItem[] = [
    ...events.map((event): ActivityItem => ({ kind: 'event', at: event.created_at, event })),
    ...prompts.map((prompt): ActivityItem => ({ kind: 'prompt', at: prompt.sent_at ?? prompt.created_at, prompt })),
    ...messages.map((message): ActivityItem => ({ kind: 'message', at: message.created_at, message })),
  ];
  return items.sort((a, b) => {
    if (a.at !== b.at) return a.at < b.at ? 1 : -1;
    return TIE_RANK[a.kind] - TIE_RANK[b.kind];
  });
}

export interface MessageStatusMeta {
  label: string;
  chip: string;
}

/** `would_send` is the dry-run outcome (`SMS_DRY_RUN`): logged, never sent. */
export function messageStatusMeta(status: MessageStatus): MessageStatusMeta {
  switch (status) {
    case 'queued':
      return { label: 'Queued', chip: 'bg-info-blue-bg text-info-blue' };
    case 'would_send':
      // Dark text on the amber tint: `text-warning` on `bg-warning-bg` is ~3.5:1, under AA for 12 px.
      return { label: 'Dry run', chip: 'bg-warning-bg text-gray-800' };
    case 'sent':
      return { label: 'Sent', chip: 'bg-success-bg text-success' };
    case 'failed':
      return { label: 'Failed', chip: 'bg-danger-bg text-danger' };
    case 'received':
      return { label: 'Received', chip: 'bg-page text-gray-700' };
    default:
      return { label: String(status), chip: 'bg-page text-gray-700' };
  }
}
